import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Activity, CheckCircle2, Terminal } from 'lucide-react';

const BOOT_SEQUENCE = [
    { label: 'INIT', text: 'Opening Woravut portfolio runtime', delay: 120, tone: 'neutral' },
    { label: 'RESUME', text: 'Indexing bilingual profile, projects, awards', delay: 420, tone: 'ok' },
    { label: 'PROJECTS', text: 'Loading production systems: WMS, internal portals, CMS platforms', delay: 780, tone: 'ok' },
    { label: 'IMPACT', text: 'Compiling experience across freelance and enterprise roles', delay: 1140, tone: 'ok' },
    { label: 'GITHUB', text: 'Syncing contribution graph and open-source activity', delay: 1500, tone: 'ok' },
    { label: 'CERTS', text: 'Verifying certificates and technical credentials', delay: 1860, tone: 'ok' },
    { label: 'REACT', text: 'Hydrating interface, particles, and theme controls', delay: 2220, tone: 'ok' },
    { label: 'READY', text: 'Premium terminal handoff complete', delay: 2520, tone: 'complete' }
];

const TOTAL_DURATION = 2800;
const TIMER_INTERVAL = 16;

function AmbientOrbs() {
    return (
        <>
            <motion.div
                className="absolute -top-32 -left-20 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl"
                animate={{ x: [0, 40, 0], y: [0, 30, 0] }}
                transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
                aria-hidden="true"
            />
            <motion.div
                className="absolute top-1/3 -right-24 h-80 w-80 rounded-full bg-purple-500/20 blur-3xl"
                animate={{ x: [0, -30, 0], y: [0, -25, 0] }}
                transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
                aria-hidden="true"
            />
            <motion.div
                className="absolute -bottom-24 left-1/4 h-64 w-64 rounded-full bg-cyan-500/15 blur-3xl"
                animate={{ x: [0, 25, 0], y: [0, -20, 0] }}
                transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
                aria-hidden="true"
            />
            <motion.div
                className="absolute bottom-0 right-1/4 h-56 w-56 rounded-full bg-pink-500/10 blur-3xl"
                animate={{ x: [0, -20, 0], y: [0, 20, 0] }}
                transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
                aria-hidden="true"
            />
        </>
    );
}

export default function MinimalLoader({ onFinish }) {
    const [progress, setProgress] = useState(0);
    const [visibleLines, setVisibleLines] = useState([]);
    const finishedRef = useRef(false);

    useEffect(() => {
        const finishOnce = () => {
            if (finishedRef.current) return;
            finishedRef.current = true;
            onFinish();
        };

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') finishOnce();
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onFinish]);

    useEffect(() => {
        const finishOnce = () => {
            if (finishedRef.current) return;
            finishedRef.current = true;
            onFinish();
        };

        const startedAt = Date.now();
        const progressTimer = setInterval(() => {
            const elapsed = Date.now() - startedAt;
            const nextProgress = Math.min(100, (elapsed / TOTAL_DURATION) * 100);
            setProgress(nextProgress);

            if (nextProgress >= 100) {
                clearInterval(progressTimer);
            }
        }, TIMER_INTERVAL);

        const textTimers = BOOT_SEQUENCE.map((item, index) => {
            return setTimeout(() => {
                setVisibleLines(prev => [...prev, item]);

                if (index === BOOT_SEQUENCE.length - 1) {
                    setTimeout(finishOnce, TOTAL_DURATION - item.delay);
                }
            }, item.delay);
        });

        return () => {
            clearInterval(progressTimer);
            textTimers.forEach(clearTimeout);
        };
    }, [onFinish]);

    return (
        <motion.div
            className="fixed inset-0 z-[200] bg-[#020617] flex flex-col items-center justify-center p-4 sm:p-8 overflow-hidden"
            exit={{
                y: '-100%',
                opacity: 0,
                transition: { duration: 0.72, ease: [0.76, 0, 0.24, 1] }
            }}
        >
            <div className="absolute inset-0 opacity-30 bg-[linear-gradient(rgba(148,163,184,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.05)_1px,transparent_1px)] bg-[size:36px_36px]" />
            <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent,rgba(15,23,42,0.35),transparent)]" />
            <AmbientOrbs />

            <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: 'easeOut' }}
                className="relative flex w-full max-w-2xl flex-col items-center"
            >
                <div className="relative w-full">
                    <div className="relative w-full overflow-hidden rounded-lg border border-white/10 bg-slate-950/95 shadow-2xl shadow-cyan-950/30 font-mono">
                        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.06),transparent_28%,rgba(16,185,129,0.04))]" />
                        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-300/60 to-transparent" />
                        <motion.div
                            className="pointer-events-none absolute inset-x-0 h-12 bg-cyan-300/5"
                            animate={{ y: [-48, 360] }}
                            transition={{ duration: 2.8, repeat: Infinity, ease: 'linear' }}
                        />

                        <div className="relative flex items-center justify-between gap-3 border-b border-white/10 bg-slate-900/80 px-4 py-3 sm:px-5">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-emerald-400/20 bg-emerald-400/10 text-emerald-300">
                                    <Terminal className="h-4 w-4" />
                                </div>
                                <div className="min-w-0">
                                    <div className="text-[11px] sm:text-xs font-bold uppercase text-slate-400">
                                        woravut.dev / resume-loader
                                    </div>
                                    <div className="mt-0.5 flex items-center gap-1.5 text-[10px] uppercase text-emerald-300">
                                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.9)]" />
                                        terminal online
                                    </div>
                                </div>
                            </div>
                            <div className="hidden sm:flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-[10px] font-bold uppercase text-cyan-200">
                                <Activity className="h-3.5 w-3.5" />
                                live context
                            </div>
                        </div>

                        <div className="relative flex h-[310px] sm:h-[340px] flex-col justify-between p-4 sm:p-6">
                            <div className="grid grid-cols-3 gap-2 text-[10px] uppercase text-slate-500">
                                <div className="rounded-md border border-white/10 bg-white/[0.03] px-2 py-2">
                                    <div className="text-slate-600">Mode</div>
                                    <div className="mt-1 text-emerald-300">Resume</div>
                                </div>
                                <div className="rounded-md border border-white/10 bg-white/[0.03] px-2 py-2">
                                    <div className="text-slate-600">Stack</div>
                                    <div className="mt-1 text-cyan-300">React</div>
                                </div>
                                <div className="rounded-md border border-white/10 bg-white/[0.03] px-2 py-2">
                                    <div className="text-slate-600">Status</div>
                                    <div className="mt-1 text-blue-300">Sync</div>
                                </div>
                            </div>

                            <div className="space-y-2.5 overflow-hidden pt-4 text-[11px] leading-relaxed sm:text-sm">
                                {visibleLines.map((line, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, x: -8 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.22 }}
                                        className={`flex items-start gap-2 break-words ${
                                            line.tone === 'complete'
                                                ? 'text-blue-300'
                                                : line.tone === 'ok'
                                                    ? 'text-emerald-300'
                                                    : 'text-slate-300'
                                        }`}
                                    >
                                        <span className="mt-0.5 text-cyan-500">{'>'}</span>
                                        <span className="min-w-[58px] text-[10px] font-bold uppercase text-slate-500 sm:min-w-[76px]">
                                            {line.label}
                                        </span>
                                        <span className="flex-1">{line.text}</span>
                                        {line.tone !== 'neutral' && (
                                            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" />
                                        )}
                                    </motion.div>
                                ))}

                                {progress < 100 && (
                                    <motion.div
                                        animate={{ opacity: [1, 0] }}
                                        transition={{ repeat: Infinity, duration: 0.8 }}
                                        className="ml-4 inline-block h-4 w-2 translate-y-1 bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.7)]"
                                    />
                                )}
                            </div>

                            <div>
                                <div className="mb-2 flex justify-between text-[10px] font-bold uppercase text-slate-500">
                                    <span>interface_boot_progress</span>
                                    <span className="text-emerald-300">{Math.round(progress)}%</span>
                                </div>
                                <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-slate-800">
                                    <motion.div
                                        className="h-full rounded-full bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-400 shadow-[0_0_16px_rgba(34,211,238,0.45)]"
                                        initial={{ width: 0 }}
                                        animate={{ width: `${progress}%` }}
                                        transition={{ duration: 0.08, ease: 'linear' }}
                                    />
                                    <motion.div
                                        className="pointer-events-none absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/50 to-transparent"
                                        animate={{ x: ['-100%', '300%'] }}
                                        transition={{ duration: 1.6, repeat: Infinity, ease: 'linear' }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8 }}
                    className="relative mt-5 text-[10px] text-slate-600 font-mono uppercase"
                >
                    Press ESC to bypass loader
                </motion.div>
            </motion.div>
        </motion.div>
    );
}
